import React, { useState, useEffect, useCallback } from 'react';
import { classificationService } from '../../services/classificationService';

const SubcategoryForm = ({
  isOpen,
  onClose,
  subcategory = null,
  onSuccess,
  apiService = classificationService,
  /** Si se pasa, no se hace GET de categorías (p. ej. RecetaClassificationContext) */
  preloadedCategories,
}) => {
  const [formData, setFormData] = useState({
    category_id: '',
    code: '',
    name: '',
    description: '',
    is_active: true,
  });
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [codeGenerated, setCodeGenerated] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const selectableCategories = categories.filter((cat) => cat?.receta_category_id == null);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await apiService.getCategories({ active_only: false });
      if (response.success) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, [apiService]);

  // Categorías: datos precargados o una petición
  useEffect(() => {
    if (!isOpen) return;
    if (Array.isArray(preloadedCategories)) {
      setCategories(preloadedCategories);
      setLoadingCategories(false);
      return;
    }
    loadCategories();
  }, [isOpen, preloadedCategories, loadCategories]);

  useEffect(() => {
    if (subcategory) {
      // Modo edición
      setFormData({
        category_id: subcategory.category_id || subcategory.category?.id || '',
        code: subcategory.code || '',
        name: subcategory.name || '',
        description: subcategory.description || '',
        is_active: subcategory.is_active !== undefined ? subcategory.is_active : true,
      });
      setCodeGenerated(true);
    } else {
      // Modo creación - generar código alfanumérico aleatorio
      const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let randomCode = '';
      for (let i = 0; i < 4; i++) {
        randomCode += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      setFormData({
        category_id: '',
        code: randomCode,
        name: '',
        description: '',
        is_active: true,
      });
      setCodeGenerated(true);
    }
    setErrors({});
  }, [subcategory, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Para el código: alfanumérico (números y letras), máximo 4 caracteres
    if (name === 'code') {
      // Solo permitir números y letras, convertir a mayúsculas
      processedValue = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      // Limitar a 4 caracteres
      if (processedValue.length > 4) {
        processedValue = processedValue.slice(0, 4);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };
  
  const generateRandomCode = () => {
    const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let randomCode = '';
    for (let i = 0; i < 4; i++) {
      randomCode += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setFormData(prev => ({
      ...prev,
      code: randomCode,
    }));
    setCodeGenerated(true);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.category_id) {
      newErrors.category_id = 'La categoría es obligatoria';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'El código es obligatorio';
    } else if (formData.code.length !== 4) {
      newErrors.code = 'El código debe tener exactamente 4 caracteres';
    } else if (!/^[0-9A-Z]{4}$/.test(formData.code)) {
      newErrors.code = 'El código debe contener solo números y letras';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    } else if (formData.name.length > 100) {
      newErrors.name = 'El nombre no puede tener más de 100 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    console.log('📝 [SubcategoryForm] Enviando formulario...');
    console.log('📦 [SubcategoryForm] Datos:', formData);

    try {
      let response;
      if (subcategory) {
        // Actualizar - no enviar código ni category_id (no se pueden modificar)
        const { code, category_id, ...updateData } = formData;
        console.log('🔄 [SubcategoryForm] Actualizando subcategoría:', subcategory.id);
        response = await apiService.updateSubcategory(subcategory.id, updateData);
        console.log('✅ [SubcategoryForm] Subcategoría actualizada exitosamente');
      } else {
        // Crear - no enviar código (se genera en el backend)
        const { code, ...createData } = formData;
        console.log('➕ [SubcategoryForm] Creando nueva subcategoría');
        response = await apiService.createSubcategory(createData);
        console.log('✅ [SubcategoryForm] Subcategoría creada exitosamente');
      }

      console.log('📊 [SubcategoryForm] Respuesta:', response);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        // Manejar errores del servidor
        if (response.errors) {
          setErrors(response.errors);
        } else {
          setErrors({ submit: response.message || 'Error al guardar la subcategoría' });
        }
      }
    } catch (error) {
      console.error('❌ [SubcategoryForm] Error:', error);
      console.error('📊 [SubcategoryForm] Respuesta del servidor:', error.response?.data);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Error al guardar la subcategoría' });
      }
    } finally {
      setLoading(false);
      console.log('🏁 [SubcategoryForm] Proceso finalizado');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {subcategory ? 'Editar Subcategoría' : 'Nueva Subcategoría'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {errors.submit && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md text-sm border border-red-200">
              {errors.submit}
            </div>
          )}

          {/* Categoría */}
          <div className="mb-4">
            <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-1">
              Categoría <span className="text-red-500">*</span>
            </label>
            <select
              id="category_id"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              disabled={loadingCategories || !!subcategory}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.category_id
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              } ${loadingCategories || subcategory ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">Seleccione una categoría</option>
              {selectableCategories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.code} - {cat.name}
                </option>
              ))}
            </select>
            {errors.category_id && (
              <p className="mt-1 text-sm text-red-600">{errors.category_id}</p>
            )}
            {loadingCategories && (
              <p className="mt-1 text-xs text-gray-500">Cargando categorías...</p>
            )}
            {subcategory && (
              <p className="mt-1 text-xs text-gray-500">La categoría no se puede modificar</p>
            )}
          </div>

          {/* Código */}
          <div className="mb-4">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                maxLength={4}
                readOnly={!!subcategory} // Solo lectura en modo edición
                className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.code
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-teal-500'
                } ${subcategory ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="A1B2"
              />
              {!subcategory && (
                <button
                  type="button"
                  onClick={generateRandomCode}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm"
                  title="Generar nuevo código"
                >
                  🔄
                </button>
              )}
            </div>
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {subcategory ? 'El código no se puede modificar' : '4 caracteres alfanuméricos (se genera automáticamente)'}
            </p>
          </div>

          {/* Nombre */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={100}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
              placeholder="Ej: Smartphones"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Máximo 100 caracteres</p>
          </div>

          {/* Descripción */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Descripción de la subcategoría (opcional)"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Estado Activo */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700">Subcategoría activa</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : subcategory ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubcategoryForm;

